import { useMutation, useQuery } from "@tanstack/react-query";
import queryString from "query-string";

import { api } from "@services/index";
import {
  ApiErrorModel,
  QueryTopicModelDto,
  QueryTopicModel,
  QueriesResponseModelDto,
  QueriesResponseModel,
  QueryModelDto,
  QueryModel,
  QueryScopeType,
  QueryStatusType,
  QuerySoftwareFormInterface,
  QuerySoftwareFormInterfaceDto,
  QueryAccountSubmissionFormInterface,
  QueryAccountSubmissionFormInterfaceDto,
  FileModel,
} from "@interfaces/index";

const prepareQueryToModel = (query: QueryModelDto): QueryModel => ({
  uniqueId: query.unique_id,
  topicId: query.topic_id,
  type: query.type,
  status: query.status,
  subject: query.subject,
  callRequested: query.call_requested,
  createdAt: query.created_at,
  messagesCount: query.messages_count,
  lastMessage: !query.last_message
    ? undefined
    : {
        body: query.last_message.body,
        author: query.last_message.author,
        sender: query.last_message.sender,
        sentAt: query.last_message.sent_at,
      },
  messages: !query.messages?.length
    ? []
    : query.messages.map((message) => ({
        ...message,
        sentAt: message.sent_at,
        links: message.links || [],
        attachments: !message.attachments?.length
          ? []
          : message.attachments.map((attachment) => ({
              uniqueId: attachment.unique_id,
              originalName: attachment.original_name,
              url: attachment.url,
              size: attachment.size,
            })),
      })),
});

export const useQueryGetTopics = () => {
  const queryFn = async () => {
    const { data: dataDto } =
      await api.get<QueryTopicModelDto[]>("/query-topics");

    return dataDto?.map(({ id, title }) => ({ id: String(id), title })) || [];
  };

  return useQuery<QueryTopicModel[], ApiErrorModel>({
    queryKey: ["queryGetTopics"],
    queryFn,
  });
};

export const useQueryGetQueries = ({
  businessId = "",
  startDate = "",
  endDate = "",
  scope = "",
  status = "",
}: {
  businessId?: string;
  startDate?: string;
  endDate?: string;
  scope?: QueryScopeType | "";
  status?: QueryStatusType | "";
}) => {
  const queryFn = async () => {
    const params = queryString.stringify(
      {
        user_business_unique_id: businessId,
        start_date: startDate ? `${startDate}T00:00:00` : "",
        end_date: endDate ? `${endDate}T23:59:59` : "",
      },
      { skipEmptyString: true },
    );
    const { data: dataDto } = await api.get<QueriesResponseModelDto>(
      `/queries?${params}`,
    );
    const queries = [
      ...dataDto.account.raised_by_client.data,
      ...dataDto.account.raised_by_fbtc.data,
      ...dataDto.submission.raised_by_client.data,
      ...dataDto.submission.raised_by_fbtc.data,
    ].filter(
      (query) =>
        (!scope || query.scope.value === scope) &&
        (!status || query.status.value === status),
    );

    // const tabs = Array.from(
    //   new Map(queries.map(({ scope }) => [scope.value, scope])).values(),
    // );   it would be better, but it doesn't work if you get from backend only 1 tab
    const submission_count =
      Number(dataDto.submission.raised_by_client.counts.awaiting_client || 0) +
      Number(dataDto.submission.raised_by_fbtc.counts.awaiting_client || 0);
    const account_count =
      Number(dataDto.account.raised_by_client.counts.awaiting_client || 0) +
      Number(dataDto.account.raised_by_fbtc.counts.awaiting_client || 0);
    const tabs: QueriesResponseModel["tabs"] = [
      {
        value: "submission",
        label: "Submission queries",
        count: submission_count,
      },
      {
        value: "account",
        label: "Account queries",
        count: account_count,
      },
    ];
    // const subtabs = Array.from(
    //   new Map(queries.map(({ status }) => [status.value, status])).values(),
    // );   it would be better, but it doesn't work if you get from backend only 1 tab
    const awaiting_client_count = !scope
      ? 0
      : Number(
          dataDto[scope as QueryScopeType].raised_by_client.counts
            .awaiting_client || 0,
        ) +
        Number(
          dataDto[scope as QueryScopeType].raised_by_fbtc.counts
            .awaiting_client || 0,
        );
    const subtabs: QueriesResponseModel["subtabs"] = [
      {
        value: "awaiting_client",
        label: "Awaiting client",
        count: awaiting_client_count,
      },
      {
        value: "awaiting_fbtc",
        label: "Awaiting FBTC",
      },
      {
        value: "resolved",
        label: "Resolved",
      },
    ];
    // const raisedBy = Array.from(
    //   new Map(queries.map(({ source }) => [source.value, source])).values(),
    // );   it would be better, but it doesn't work if you get from backend only 1 tab
    const raisedBy: QueriesResponseModel["raisedBy"] = [
      {
        value: "raised_by_fbtc",
        label: "Raised by FBTC",
      },
      {
        value: "raised_by_client",
        label: "Raised by me",
      },
    ];
    const raisedByFbtc = queries
      .filter(({ source }) => source.value === "raised_by_fbtc")
      .map((query) => prepareQueryToModel(query));
    const raisedByClient = queries
      .filter(({ source }) => source.value === "raised_by_client")
      .map((query) => prepareQueryToModel(query));

    return {
      tabs,
      subtabs,
      raisedBy,
      raisedByFbtc,
      raisedByClient,
    };
  };

  return useQuery<QueriesResponseModel, ApiErrorModel>({
    queryKey: [
      "queryGetQueries",
      businessId,
      startDate,
      endDate,
      scope,
      status,
    ],
    queryFn,
    refetchOnWindowFocus: true,
  });
};

export const useQuerySoftware = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    phone,
    message,
    userId,
    browserVersion,
    files,
  }: QuerySoftwareFormInterface) => {
    const fieldsDto: QuerySoftwareFormInterfaceDto = {
      phone,
      message,
      user_id: userId,
      browser_version: browserVersion,
      files: files?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
      })),
    };
    await api.post("/report-problem", fieldsDto);
  };

  return useMutation({
    mutationKey: ["querySoftware"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        phone: error.fields?.phone || "",
        message: error.fields?.message || "",
        userId: error.fields?.user_id || "",
        browserVersion: error.fields?.browser_version || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useQueryAccountSubmission = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    category,
    topicId,
    subject,
    message,
    callRequested,
    submissionId,
  }: QueryAccountSubmissionFormInterface) => {
    const fieldsDto: QueryAccountSubmissionFormInterfaceDto = {
      scope: category,
      topic_id: topicId,
      subject,
      message,
      call_requested: !!callRequested,
      submission_unique_id: submissionId,
    };
    await api.post("/queries", fieldsDto);
  };

  return useMutation({
    mutationKey: ["queryAccountSubmission"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        category: error.fields?.scope || "",
        topicId: error.fields?.topic_id || "",
        subject: error.fields?.subject || "",
        message: error.fields?.message || "",
        callRequested: error.fields?.call_requested || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useQueryGetOne = (id: string) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<QueryModelDto>(`/queries/${id}`);

    return prepareQueryToModel(dataDto);
  };

  return useQuery<QueryModel, ApiErrorModel>({
    queryKey: ["queryGetOne", id],
    queryFn,
    enabled: !!id,
  });
};

export const useQueryReply = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    queryId,
    message,
    files,
  }: {
    queryId: string;
    message: string;
    files?: FileModel[];
  }) => {
    const fieldsDto = {
      message,
      files: files?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
      })),
    };
    await api.post(`/queries/${queryId}/messages`, fieldsDto);
  };

  return useMutation({
    mutationKey: ["queryReply"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        message: error.fields?.message || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};
